import type { components, CountdownApiClient } from "@countdown/api-client";
import { type ReactElement, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useMatch,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router";

import { ConfirmButton } from "@/components/ConfirmButton";
import { HelpTip } from "@/components/HelpTip";
import { LinkRow } from "@/components/LinkRow";
import { PageHeading } from "@/components/PageHeading";
import { Notice } from "@/components/Notice";
import { StatusBadge, type StatusTone, Tick } from "@/components/StatusBadge";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  type ActionDetail,
  type ActionOutcome,
  LOAD_FAILED,
  ROLE_MISSING,
  loadActionDetail,
  loadFields,
  moveAction,
  saveActionSection,
  useResource,
} from "@/lib/api";
import { type Clock, systemClock } from "@/lib/clock";
import { useStoredColumns } from "@/lib/columns";
import {
  type OpenTask,
  fieldChecklist,
  moduleSummary,
  openTasks,
  progressOf,
} from "@/lib/door";
import { HELP_TIPS } from "@/lib/help";
import {
  ACTION_STATUS_LABELS,
  BOOKING_WORKFLOW,
  BOOKING_WORKFLOW_BY_STATE,
  DESIGN_TAB_ELEMENTS,
  NUMBER_FORMAT,
  SETTINGS_TAB_ELEMENTS,
  type ActionStatus,
  can,
  formatOpening,
  notArchived,
} from "@/lib/labels";
import { navLinkClassName } from "@/lib/utils";
import { SPONSOR_WORDING, type Wording, firmPath, wordingOf } from "@/lib/wording";
import { CalendarPreviewPage } from "@/pages/CalendarPreviewPage";

type ActionView = components["schemas"]["BackofficeActionView"];
type FieldView = components["schemas"]["BackofficeFieldView"];
type SponsorView = components["schemas"]["BackofficeSponsorView"];

/** What the pages below an action read, so none of them loads the action or the overview again. */
export interface ActionContext {
  readonly action: ActionView;
  /** Every action of the cell but the archived ones, as the overview holds them. */
  readonly actions: readonly ActionView[];
  readonly fields: readonly FieldView[];
  readonly reloadFields: () => void;
  readonly reloadOverview: () => void;
  readonly sponsors: readonly SponsorView[];
  readonly granted: readonly string[];
  /** The superadmin sets link and schedule and archives a draft (02.11.2026). */
  readonly superadmin: boolean;
  /** `https://<participant host>` of the tenant; `null` while none is registered (D36). */
  readonly participantBaseUrl: string | null;
}

export function useActionContext(): ActionContext {
  return useOutletContext<ActionContext>();
}

/**
 * The action's detail for a tab (Gestaltung, Einstellungen), loaded again when a step in the action's header
 * (go live, end, archive) changes the status: the tabs read their locks from it. `reload` keeps
 * the loaded state, so unsaved input in the other forms stays.
 */
export function useActionDetail(): ReturnType<typeof useResource<ActionDetail>> {
  const { action } = useActionContext();
  const load = useCallback(
    (api: CountdownApiClient) => loadActionDetail(api, action.action_id),
    [action.action_id],
  );
  const detail = useResource<ActionDetail>(load);
  const { reload } = detail;
  const status = action.status;
  const loaded = useRef(status);
  useEffect(() => {
    if (loaded.current !== status) {
      loaded.current = status;
      reload();
    }
  }, [status, reload]);
  return detail;
}


/** What a tab says while its detail is not there. */
export function DetailState({ status }: { readonly status: string }): ReactElement {
  return (
    <p className="text-sm text-muted-foreground">
      {status === "forbidden" ? ROLE_MISSING : status === "loading" ? "Wird geladen …" : LOAD_FAILED}
    </p>
  );
}

const STATUS_TONES: Readonly<Record<ActionStatus, StatusTone>> = {
  draft: "outline",
  scheduled: "neutral",
  live: "active",
  paused: "warning",
  finished: "success",
  archived: "neutral",
};

/** Status of an action with its switches, the same in the list and on the action's page. */
export function ActionBadges({
  action,
}: {
  readonly action: ActionView;
}): ReactElement {
  return (
    <span className="flex flex-wrap gap-2">
      <StatusBadge tone={STATUS_TONES[action.status]}>
        {ACTION_STATUS_LABELS[action.status]}
      </StatusBadge>
      {action.is_test ? <StatusBadge tone="outline">Testaktion</StatusBadge> : null}
      {action.paused ? <StatusBadge tone="danger">Kalender angehalten</StatusBadge> : null}
      {action.dispatch_stopped ? (
        <StatusBadge tone="danger">Versand angehalten</StatusBadge>
      ) : null}
      {/* Today's calendar mail only when part of it failed: the one alarm on screen (D48). */}
      {(action.mail_today?.failed ?? 0) > 0 ? (
        <StatusBadge tone="danger">
          Tagesmail: {NUMBER_FORMAT.format(action.mail_today?.failed ?? 0)} fehlgeschlagen
        </StatusBadge>
      ) : null}
    </span>
  );
}

/**
 * What stands in the way of a live campaign.
 *
 * The list comes from the server verbatim, the same function `campaign publish`
 * uses, so the screen cannot disagree with the check that actually decides.
 * Only obstacles: the warnings stop nothing, and a line under every action that
 * stops nothing is the kind of text nobody reads twice.
 *
 * A to-do, not an error (D27: red only for errors and final steps), with the one way to the
 * place where most of them are fixed, for whoever may go there. The fallback content is fixed
 * in the design tab (D88), so its line links there.
 */
function Blockers({
  action,
  settings,
  fallback,
}: {
  readonly action: ActionView;
  readonly settings: boolean;
  readonly fallback: boolean;
}): ReactElement | null {
  if (action.obstacles.length === 0) {
    return null;
  }
  return (
    <Notice tone="info" className="mt-3">
      <p className="font-medium">Vor dem Livegang fehlt noch:</p>
      <ul className="mt-1 list-disc pl-5">
        {action.obstacles.map((entry) => (
          <li key={entry}>
            {/* The server's wording (`campaign.py`, go-live obstacles). */}
            {fallback && entry === "Ersatzinhalt fehlt" ? (
              <Link
                className="underline underline-offset-4"
                to="gestaltung?ansicht=fenster&tuerchen=unbelegt"
              >
                {entry}
              </Link>
            ) : (
              entry
            )}
          </li>
        ))}
      </ul>
      {settings ? (
        <Link className="mt-2 inline-block underline underline-offset-4" to="einstellungen">
          Zu den Einstellungen
        </Link>
      ) : null}
    </Notice>
  );
}

/** One step of the header: a button, its run, and the server's refusal under it. */
function StepButton({
  label,
  variant = "default",
  disabled = false,
  run,
  onDone,
}: {
  readonly label: string;
  readonly variant?: "default" | "outline";
  readonly disabled?: boolean;
  readonly run: () => Promise<ActionOutcome>;
  readonly onDone: () => void;
}): ReactElement {
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  return (
    <div className="grid justify-items-start gap-2">
      <Button
        variant={variant}
        disabled={disabled || busy}
        onClick={() => {
          setBusy(true);
          setRefusal(null);
          run()
            .then((outcome) => {
              if (outcome.ok) {
                onDone();
              } else {
                setRefusal(outcome.message ?? LOAD_FAILED);
              }
            })
            .catch((error: unknown) => {
              console.error("[ActionPage] step failed", label, error);
              setRefusal(LOAD_FAILED);
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        {busy ? "Wird ausgeführt …" : label}
      </Button>
      {refusal === null ? null : (
        <Notice tone="error" className="whitespace-pre-line">
          {refusal}
        </Notice>
      )}
    </div>
  );
}

/** The calendar day of `action` today, as `YYYY-MM-DD` in the action's own time zone. */
function todayOf(action: ActionView, clock: Clock): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: action.timezone }).format(clock.now());
}

/**
 * The one status step of an action, in its header (19.10.2026), with the publish permission:
 * a draft goes live or changes its test mark, an ended run is finished, a finished action is
 * archived. Until then they stood at the foot of a long settings page, out of reach for a
 * publish permission without design. The two steps that cannot be undone ask once more (D27).
 *
 * "Endgültig beenden" only appears where it can be meant: for a test action, or once the run is
 * over. A red button over every running calendar all December would be a misclick waiting to
 * happen; the server still decides and answers in words (follow-up period, D13).
 */
export function ActionLifecycle({
  action,
  granted,
  onDone,
  clock = systemClock,
}: {
  readonly action: ActionView;
  readonly granted: readonly string[];
  readonly onDone: () => void;
  /** Today, for whether the run is over; a fixed clock in tests. */
  readonly clock?: Clock;
}): ReactElement | null {
  if (action.status === "draft") {
    const testFlag = can(granted, "action_test_flag");
    const publish = can(granted, "action_publish");
    if (!testFlag && !publish) {
      return null;
    }
    return (
      <div className="flex flex-wrap items-start gap-2">
        {testFlag ? (
          <div className="flex items-center gap-2.5">
            <StepButton
              label={action.is_test ? "Testmarkierung entfernen" : "Als Testaktion markieren"}
              variant="outline"
              run={() =>
                saveActionSection("test-flag", { is_test: !action.is_test }, action.action_id)
              }
              onDone={onDone}
            />
            <HelpTip topic="Testaktion">{HELP_TIPS.testAction}</HelpTip>
          </div>
        ) : null}
        {publish ? (
          <StepButton
            label="Live schalten"
            disabled={action.obstacles.length > 0}
            run={() => moveAction("publish", action.action_id)}
            onDone={onDone}
          />
        ) : null}
      </div>
    );
  }
  if (!can(granted, "action_finish")) {
    return null;
  }
  if (
    (action.status === "live" || action.status === "paused") &&
    (action.is_test || todayOf(action, clock) > action.ends_on)
  ) {
    return (
      <ConfirmButton
        label="Endgültig beenden"
        question={`»${action.title}« endgültig beenden?`}
        consequence="Der Kalender schließt sofort und lässt sich nicht wieder öffnen. Verlosung und Lead-Abruf laufen weiter."
        confirmLabel="Endgültig beenden"
        run={() => moveAction("finish", action.action_id)}
        onDone={onDone}
      />
    );
  }
  if (action.status === "finished") {
    return (
      <ConfirmButton
        label="Archivieren"
        question={`»${action.title}« archivieren?`}
        consequence="Die Aktion wird abgelegt. Der Superadmin kann sie in den Einstellungen reaktivieren."
        confirmLabel="Archivieren"
        run={() => moveAction("archive", action.action_id)}
        onDone={onDone}
      />
    );
  }
  return null;
}

function NotFound(): ReactElement {
  return (
    <div className="flex flex-col items-start gap-3 text-sm">
      <p className="text-muted-foreground">Aktion nicht gefunden.</p>
      <Link className="underline underline-offset-4" to="/">
        Aktionen
      </Link>
    </div>
  );
}

interface ActionPageProps {
  readonly actions: readonly ActionView[];
  readonly sponsors: readonly SponsorView[];
  readonly granted: readonly string[];
  readonly superadmin: boolean;
  readonly reloadOverview: () => void;
  readonly participantBaseUrl: string | null;
}

/**
 * One action (`/aktionen/:actionId`): its header, the tabs, and the tab below.
 *
 * The action comes from the overview the shell already holds, found by the id in the address;
 * only the field list is loaded here, because it belongs to this one action. An id the overview
 * does not know is "not found", not a load error: a bookmark to an action of last season.
 */
export function ActionPage({
  actions,
  ...rest
}: ActionPageProps): ReactElement {
  const { actionId } = useParams();
  const action = actions.find((entry) => entry.action_id === actionId);
  return action === undefined ? (
    <NotFound />
  ) : (
    <LoadedAction key={action.action_id} action={action} actions={actions} {...rest} />
  );
}

function LoadedAction({
  action,
  actions,
  sponsors,
  granted,
  superadmin,
  reloadOverview,
  participantBaseUrl,
}: ActionPageProps & {
  readonly action: ActionView;
}): ReactElement {
  const load = useCallback(
    (api: CountdownApiClient) => loadFields(api, action.action_id),
    [action.action_id],
  );
  const fields = useResource<FieldView[]>(load);
  // A door is opened from the Türchenplan, so its page belongs to that tab.
  const onDoor = useMatch("/aktionen/:actionId/tuerchen/:position") !== null;
  // The plan's filter travels with the door and back (`?zustand=`).
  const { search } = useLocation();

  if (fields.status === "not_found") {
    return <NotFound />;
  }

  return (
    <>
      <PageHeading
        title={action.title}
        back={{ to: "/", label: "Alle Aktionen" }}
        actions={<ActionLifecycle action={action} granted={granted} onDone={reloadOverview} />}
      />
      <div className="-mt-3 mb-6">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <ActionBadges action={action} />
          <p className="text-sm">
            <span className="font-bold">{action.booked_fields}</span> von {action.field_count}{" "}
            belegt
          </p>
        </div>
        <Blockers
          action={action}
          settings={can(granted, ...SETTINGS_TAB_ELEMENTS)}
          fallback={can(granted, "action_fallback")}
        />
      </div>

      <nav aria-label="Aktion" className="mb-6">
        <ul className="inline-flex flex-wrap gap-1 rounded-md bg-muted p-1">
          <li>
            {onDoor ? (
              // A door is opened from the Türchenplan, so its page belongs to that tab.
              <Link
                to={{ pathname: ".", search }}
                aria-current="true"
                className={navLinkClassName({ isActive: true })}
              >
                Türchenplan
              </Link>
            ) : (
              <NavLink to="." end className={navLinkClassName}>
                Türchenplan
              </NavLink>
            )}
          </li>
          {can(granted, ...DESIGN_TAB_ELEMENTS) ? (
            <li>
              <NavLink to="gestaltung" className={navLinkClassName}>
                Gestaltung
              </NavLink>
            </li>
          ) : null}
          {can(granted, "example_view") ? (
            <li>
              <NavLink to="beispiel" className={navLinkClassName}>
                Beispielansicht
              </NavLink>
            </li>
          ) : null}
          {can(granted, ...SETTINGS_TAB_ELEMENTS) ? (
            <>
              <li>
                <NavLink to="einstellungen" className={navLinkClassName}>
                  Einstellungen
                </NavLink>
              </li>
            </>
          ) : null}
        </ul>
      </nav>

      {fields.status === "ready" ? (
        // The save bar of the shell (`App`) takes the tab's forms.
        <Outlet
          context={
            {
              action,
              actions: actions.filter(notArchived),
              fields: fields.data,
              reloadFields: fields.reload,
              reloadOverview,
              sponsors,
              granted,
              superadmin,
              participantBaseUrl,
            } satisfies ActionContext
          }
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          {fields.status === "loading" ? "Wird geladen …" : LOAD_FAILED}
        </p>
      )}
    </>
  );
}

/**
 * A door placed with a sponsor nobody of whose company has had an invitation yet
 * (16.10.2026): "Eingeladen" only once the mail went out, `sponsor_invitations.py`.
 */
const INVITATION_OPEN = "Offen";
/** An invited door the publisher designs (E2): nobody is waited for, the door is not filled. */
const NOT_FILLED = "Noch leer";

interface PlanFilter {
  /** Value of `?zustand=`; empty for "all". */
  readonly key: string;
  readonly label: string;
  readonly matches: (field: FieldView) => boolean;
}

export const PLAN_FILTERS: readonly PlanFilter[] = [
  { key: "", label: "Alle", matches: () => true },
  {
    key: "zu-pruefen",
    label: "Zu prüfen",
    matches: (field) =>
      field.booking_state === "submitted" || field.booking_state === "in_review",
  },
  {
    key: "unbelegt",
    label: "Unbelegt",
    matches: (field) => field.booking_state === null,
  },
  {
    key: "offen",
    label: INVITATION_OPEN,
    matches: (field) => doorStateLabel(field) === INVITATION_OPEN,
  },
  {
    // A request waits for the sponsor's approval, overdue or not (H2, E5).
    key: "beim-sponsor",
    label: "Beim Sponsor",
    matches: (field) =>
      field.sponsor_approval === "waiting" || field.sponsor_approval === "overdue",
  },
  {
    key: "noch-leer",
    label: NOT_FILLED,
    matches: (field) => doorStateLabel(field) === NOT_FILLED,
  },
  ...BOOKING_WORKFLOW.map((step) => ({
    key: step.state,
    label: step.label,
    matches: (field: FieldView) =>
      field.booking_state === step.state &&
      (step.state !== "invited" || doorStateLabel(field) === step.label),
  })),
];

/** The figures of the plan: shown on request, they make the table twice as wide. */
const PLAN_COLUMNS = { counts: false } as const;

/** Door, progress, booking, sponsor, modules, opening: the columns that always stand. */
const PLAN_BASE_COLUMNS = 6;

/**
 * How far a booked door is (D94): a green tick when every line of its checklist is done, else
 * the percent in the colours of a traffic light. An unbooked door says nothing.
 */
export function DoorProgress({ field }: { readonly field: FieldView }): ReactNode {
  const items = fieldChecklist(field);
  if (items === null) {
    return null;
  }
  const percent = progressOf(items);
  if (percent === 100) {
    return <Tick done label="Vollständig" />;
  }
  const open = items.filter((item) => !item.done).map((item) => item.label);
  return (
    <span
      title={`Offen: ${open.join(", ")}`}
      className={`font-medium tabular-nums ${percent < 50 ? "text-destructive" : "text-notice"}`}
    >
      {percent} %
    </span>
  );
}

/**
 * What is still to do in this action (D94), one line per step with the way to it, like a short
 * ticket list. Derived from the rows (`openTasks`), so a done step is gone with the next reload;
 * nothing stands when nothing is open.
 */
function OpenTasks({ tasks }: { readonly tasks: readonly OpenTask[] }): ReactNode {
  if (tasks.length === 0) {
    return null;
  }
  return (
    <section aria-labelledby="offene-aufgaben" className="mb-6 border-l-4 border-notice pl-4">
      <h3 id="offene-aufgaben" className="text-base font-bold">
        Offene Aufgaben <span className="tabular-nums text-muted-foreground">{tasks.length}</span>
      </h3>
      <ul className="mt-2 grid max-h-64 gap-1 overflow-y-auto text-sm">
        {tasks.map((task) => (
          <li key={task.key}>
            <Link className="underline underline-offset-4" to={task.to}>
              {task.text}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Which view of the plan a visit shows: the calendar (the participant page in a frame, D48) only
 * for whoever may mint its preview token (`doors`), the list for everyone else, whatever the
 * address says.
 */
export function planView(granted: readonly string[], search: URLSearchParams): "list" | "calendar" {
  return can(granted, "door_content") && search.get("ansicht") === "kalender"
    ? "calendar"
    : "list";
}

/** "Liste | Kalender" above the plan; the filter stays in the address while the calendar shows. */
function PlanViews({
  calendar,
  onChange,
}: {
  readonly calendar: boolean;
  readonly onChange: (calendar: boolean) => void;
}): ReactElement {
  return (
    <div
      role="group"
      aria-label="Ansicht"
      className="mb-4 inline-flex gap-1 rounded-md bg-muted p-1"
    >
      {[
        { label: "Liste", value: false },
        { label: "Kalender", value: true },
      ].map((view) => (
        <button
          key={view.label}
          type="button"
          aria-pressed={calendar === view.value}
          className={navLinkClassName({ isActive: calendar === view.value })}
          onClick={() => {
            onChange(view.value);
          }}
        >
          {view.label}
        </button>
      ))}
    </div>
  );
}

/** The first three chips always stand; a state gets its chip only while a door is in it. */
const FIXED_FILTERS = 3;

/**
 * The chips above the plan, with their counts. A customer's calendar has no approval way
 * (E5), so nothing in it waits with its sponsor and "Beim Sponsor" never stands there.
 */
export function planChips(
  fields: readonly FieldView[],
  customer: boolean,
): readonly (PlanFilter & { readonly count: number })[] {
  return PLAN_FILTERS.map((filter) => ({
    ...filter,
    count: fields.filter(filter.matches).length,
  })).filter(
    (chip, index) =>
      (index < FIXED_FILTERS || chip.count > 0) && !(customer && chip.key === "beim-sponsor"),
  );
}

/**
 * The Türchenplan: every door of the action with its booking, its opening and its figures.
 * The booking leads because it is what the plan is opened for; the dates follow it.
 *
 * It replaces the booking counters, the review list and the field table that were three
 * screens: "Zu prüfen" is the review list. The filter lives in the address (`?zustand=`), so
 * a filtered plan can be bookmarked and survives opening a door and coming back. A value that
 * names no chip (a state whose last door just moved on) shows everything rather than nothing.
 */
export function FieldPlan(): ReactElement {
  const { action, fields, granted } = useActionContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [columns, showColumn] = useStoredColumns("tuerchenplan", PLAN_COLUMNS);
  const calendar = planView(granted, searchParams) === "calendar";
  const views = can(granted, "door_content") ? (
    <PlanViews
      calendar={calendar}
      onChange={(next) => {
        setSearchParams(
          (current) => {
            const params = new URLSearchParams(current);
            if (next) {
              params.set("ansicht", "kalender");
            } else {
              params.delete("ansicht");
            }
            return params;
          },
          { replace: true },
        );
      }}
    />
  ) : null;

  const chips = planChips(fields, (action.customer_sponsor_id ?? null) !== null);
  const wording = wordingOf(action);
  const tasks = openTasks(
    fields,
    (sponsorId) => `${firmPath(sponsorId, wording)}${wording.view === "" ? "?" : "&"}reiter=stammdaten`,
    action.status === "live" || action.status === "paused",
  );
  const requested = searchParams.get("zustand") ?? "";
  const active = chips.find((chip) => chip.key === requested) ?? chips[0];
  const rows = active === undefined ? fields : fields.filter(active.matches);

  // Summed in the browser rather than on the server: the rows are already here, and a total
  // the server computed separately could disagree with the rows under it.
  const total = (
    key:
      | "views"
      | "drafts"
      | "registrations_started"
      | "participations"
      | "leads",
  ): string =>
    NUMBER_FORMAT.format(rows.reduce((sum, field) => sum + field[key], 0));

  if (calendar) {
    // Mounted only while shown: the preview mints its token on mount, the list costs none.
    return (
      <>
        {views}
        <CalendarPreviewPage
          actionId={action.action_id}
          title={action.title}
          privacy={can(granted, "test_data")}
        />
      </>
    );
  }

  return (
    <>
      {views}
      <OpenTasks tasks={tasks} />
      <label className="mb-3 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="border border-input"
          checked={columns.counts}
          onChange={(event) => {
            showColumn("counts", event.target.checked);
          }}
        />
        Kennzahlen zeigen (Aufrufe, Antworten, Registrierungen, Teilnahmen, Leads)
      </label>

      <fieldset className="mb-4 flex flex-wrap gap-2">
        <legend className="sr-only">Zustand</legend>
        {chips.map((chip) => {
          const pressed = chip === active;
          return (
            <button
              key={chip.key}
              type="button"
              aria-pressed={pressed}
              className={[
                "rounded-md border px-3 py-1 text-sm",
                pressed
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border hover:bg-muted",
              ].join(" ")}
              onClick={() => {
                setSearchParams(chip.key === "" ? {} : { zustand: chip.key });
              }}
            >
              {chip.label} <span className="tabular-nums">{chip.count}</span>
            </button>
          );
        })}
      </fieldset>

      <Table>
        <TableCaption className="sr-only">Türchen der Aktion</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Türchen</TableHead>
            <TableHead>Stand</TableHead>
            <TableHead>Belegung</TableHead>
            <TableHead>{wording.nominative}</TableHead>
            <TableHead>Module</TableHead>
            <TableHead>Öffnet</TableHead>
            {columns.counts ? (
              <>
                <TableHead className="text-right">Aufrufe</TableHead>
                {/* Drafts: a quiz played again counts once, as in the sponsor's report. */}
                <TableHead className="text-right">Antworten</TableHead>
                <TableHead className="text-right whitespace-normal">
                  Begonnene Registrierungen
                </TableHead>
                <TableHead className="text-right">Teilnahmen</TableHead>
                <TableHead className="text-right">Leads</TableHead>
              </>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((field) => (
            <LinkRow key={field.position}>
              <TableCell className="font-medium">
                {/* The link is the way for keyboard and reader; the row follows it for the mouse (D48). */}
                <Link
                  className="underline underline-offset-4"
                  to={{ pathname: `tuerchen/${field.position}`, search: searchParams.toString() }}
                >
                  {field.position}
                </Link>
              </TableCell>
              <TableCell>
                <DoorProgress field={field} />
              </TableCell>
              <TableCell>
                <DoorState field={field} wording={wording} />
              </TableCell>
              <TableCell>
                <SponsorLink field={field} wording={wording} />
              </TableCell>
              <TableCell>
                {/* An unbooked door has no content of its own; "Belegung" says why. */}
                {field.booking_state === null ? null : moduleSummary(field)}
              </TableCell>
              <TableCell className="whitespace-nowrap tabular-nums">
                {formatOpening(field.opens_at)}
              </TableCell>
              {columns.counts ? (
                <>
                  <TableCell className="text-right">
                    {NUMBER_FORMAT.format(field.views)}
                  </TableCell>
                  <TableCell className="text-right">
                    {NUMBER_FORMAT.format(field.drafts)}
                  </TableCell>
                  <TableCell className="text-right">
                    {NUMBER_FORMAT.format(field.registrations_started)}
                  </TableCell>
                  <TableCell className="text-right">
                    {NUMBER_FORMAT.format(field.participations)}
                  </TableCell>
                  <TableCell className="text-right">
                    {NUMBER_FORMAT.format(field.leads)}
                  </TableCell>
                </>
              ) : null}
            </LinkRow>
          ))}
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={PLAN_BASE_COLUMNS + (columns.counts ? 5 : 0)}
                className="text-muted-foreground"
              >
                Keine Türchen.
              </TableCell>
            </TableRow>
          ) : null}
          {columns.counts ? (
            <TableRow className="font-medium">
              <TableCell colSpan={PLAN_BASE_COLUMNS}>Summe</TableCell>
              <TableCell className="text-right">{total("views")}</TableCell>
              <TableCell className="text-right">{total("drafts")}</TableCell>
              <TableCell className="text-right">
                {total("registrations_started")}
              </TableCell>
              <TableCell className="text-right">
                {total("participations")}
              </TableCell>
              <TableCell className="text-right">{total("leads")}</TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </>
  );
}

/** The sponsor of a door, as a link to the sponsor's page (a customer's under »Kunden«, E8). */
function SponsorLink({
  field,
  wording,
}: {
  readonly field: FieldView;
  readonly wording: Wording;
}): ReactNode {
  const sponsorId = field.sponsor_id ?? null;
  if (sponsorId === null) {
    return field.sponsor_company;
  }
  return (
    <Link className="underline underline-offset-4" to={firmPath(sponsorId, wording)}>
      {field.sponsor_company}
    </Link>
  );
}

/**
 * The booking's state in words; "Offen" for a placed sponsor nobody has invited yet, where the
 * sponsor is invited to edit or deliver pictures (H4). Where no invitation follows: "Noch leer".
 */
export function doorStateLabel(field: FieldView): string {
  if (field.booking_state === null) {
    return "Unbelegt";
  }
  if (field.booking_state === "invited" && !field.invites) {
    return NOT_FILLED;
  }
  if (field.booking_state === "invited" && !field.invitation_sent) {
    return INVITATION_OPEN;
  }
  return BOOKING_WORKFLOW_BY_STATE[field.booking_state].label;
}

/**
 * Where a door stands with its sponsor's approval (`sponsor_approval`, H2, E4, E5), in the
 * words of the calendar (E8).
 */
function approvalBadges(
  wording: Wording,
): Readonly<Record<NonNullable<FieldView["sponsor_approval"]>, { label: string; tone: StatusTone }>> {
  return {
    correction: { label: "Korrektur", tone: "outline" },
    waiting: { label: `Beim ${wording.oblique}`, tone: "neutral" },
    overdue: { label: "Freigabe überfällig", tone: "warning" },
    changes_requested: { label: "Änderungswunsch", tone: "neutral" },
    // Where the sponsor publishes themselves: their change, not published yet.
    sponsor_change: { label: `Änderung des ${wording.genitive}`, tone: "outline" },
  };
}

/** Where a door's booking stands, with its warnings beside it; the plan and the door page. */
export function DoorState({
  field,
  wording = SPONSOR_WORDING,
}: {
  readonly field: FieldView;
  readonly wording?: Wording;
}): ReactElement {
  const approval = field.sponsor_approval ?? null;
  const badge = approval === null ? null : approvalBadges(wording)[approval];
  return (
    <span className="flex flex-wrap items-center gap-2">
      {doorStateLabel(field)}
      {badge !== null ? <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge> : null}
      {/* An overdue request says it already: one warning for one fact. */}
      {field.deadline_overdue && approval !== "overdue" ? (
        <StatusBadge tone="warning">Frist überschritten</StatusBadge>
      ) : null}
      {field.depublished ? <StatusBadge tone="danger">Vom Netz</StatusBadge> : null}
    </span>
  );
}
